/**
 * Normalizacja wartości liczników pasażerów.
 *
 * Liczniki w UI używają +/- buttons, ale wartości mogą trafić do formularza
 * z wielu źródeł:
 * - URL search params (`?a=2.5` lub `?a=abc`),
 * - localStorage / inicjalne propsy,
 * - testy E2E z manualnym DOM injection.
 *
 * Ta funkcja jest jedynym miejscem, w którym tworzymy „bezpieczną" liczbę
 * pasażerów: zawsze całkowita, zawsze w zakresie [min, max], NaN/null/undefined/
 * /""/0.5 → fallback. Używamy jej zarówno w stanie początkowym SearchForm,
 * jak i tuż przed `nav()` na /search — defense in depth.
 */
export interface PaxRange {
  min: number;
  max: number;
  /** Wartość zwracana, gdy `value` jest niepoprawna (NaN, "", null, ułamek). */
  fallback: number;
}

export function normalizePaxCount(value: unknown, range: PaxRange): number {
  const { min, max, fallback } = range;
  // Akceptujemy number | string (np. z URL). Inne typy → fallback.
  let n: number;
  if (typeof value === "number") {
    n = value;
  } else if (typeof value === "string") {
    const trimmed = value.trim();
    if (trimmed === "") return clamp(fallback, min, max);
    n = Number(trimmed);
  } else {
    return clamp(fallback, min, max);
  }

  if (!Number.isFinite(n)) return clamp(fallback, min, max);
  // Ułamki (0.5, 1.7) są niedozwolone — odrzucamy do fallback, żeby
  // nie zaokrąglać po cichu (silent rounding mógłby zmienić intencję usera).
  if (!Number.isInteger(n)) return clamp(fallback, min, max);
  return clamp(n, min, max);
}

function clamp(n: number, min: number, max: number): number {
  if (n < min) return min;
  if (n > max) return max;
  return n;
}

/** Domyślne zakresy spójne z eSky / regulaminem (1–9 dorosłych, 0–9 dzieci, niemowlęta ≤ dorośli). */
export const PAX_RANGES = {
  adults: { min: 1, max: 9, fallback: 1 } as const,
  children: { min: 0, max: 9, fallback: 0 } as const,
  // `infants` ograniczamy dynamicznie przez liczbę dorosłych — patrz `normalizeInfants`.
  infants: { min: 0, max: 9, fallback: 0 } as const,
} as const;

/** Niemowlę musi mieć dorosłego — `max(infants) = adults`. */
export function normalizeInfants(value: unknown, adults: number): number {
  const safeAdults = normalizePaxCount(adults, PAX_RANGES.adults);
  return normalizePaxCount(value, { min: 0, max: safeAdults, fallback: 0 });
}
